/**
 * TrustGate Console - Home View
 * Ops-first landing page with governance status, KPIs, and activity stream
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where, limit } from 'firebase/firestore';
import { WorkLifecycleNav } from '../components/lifecycle/WorkLifecycleNav';
import { ActiveWorkItemBar } from '../components/lifecycle/ActiveWorkItemBar';
import { ResumeWorkPanel } from '../components/lifecycle/ResumeWorkPanel';
import { DemoToPrivatePanel } from '../workspace/session/DemoToPrivatePanel';
import { useWorkLifecycleContext } from '../components/lifecycle/useWorkLifecycleContext';
import { auth } from '../../firebase/init';
import { db } from '../../firebase/init';
import { Card } from './components/Card';
import { MetricTile } from './components/MetricTile';
import { MetricRow } from './components/MetricRow';
import { Badge } from './components/Badge';
import { ActionRequestModal } from './components/ActionRequestModal';
import { BoundIntentDetailView } from './components/BoundIntentDetailView';
import { CanonicalFlowPanel } from './components/CanonicalFlowPanel';
import {
  fetchEpochECS,
  subscribeToActivityStreamECS,
  subscribeToMetricsECS,
} from '../ecs/dataService';
import { subscribeActivePolicyPack, type ActivePolicyPack } from '../policy_packs/PolicyPacksService';
import {
  formatCurrency,
  formatRelativeTime,
  formatTimestamp,
} from '../data/TrustGateDataService';
import type {
  TrustGateEpoch,
  TrustGateMetricsECS,
  TrustGateEvent,
} from '../ecs/types';

interface ConsoleHomeProps {
  onNavigateToProofExplorer: () => void;
  onNavigateToAdmin?: () => void;
  onNavigateToCreateRequest?: () => void;
  onNavigateToMarketplace?: () => void;
  onNavigateToPolicyPacks?: () => void;
  onNavigateToRequestsInbox?: () => void;
  onNavigateToEpoch?: () => void;
  onNavigateToCivilDisclosure?: () => void;
  onNavigateToPdfEvidenceBundle?: () => void;
  onNavigateToTraceExplorer?: () => void;
  onNavigateToCryptoTest?: () => void;
  onNavigateToVK1Architecture?: () => void;
  isAdmin: boolean;
  tenantId: string;
}

type BlockedRequest = {
  id: string;
  executionBlockReasonCode?: string;
  lockedParams?: { memo?: string };
  updatedAt?: any;
};

function parseMemo(memo: string | undefined): { packId?: string; version?: string } {
  if (!memo) return {};
  try {
    const p = JSON.parse(memo);
    return { packId: p?.packId, version: p?.version };
  } catch { return {}; }
}

function safeDateLabel(ts: any): string {
  try {
    if (!ts) return '-';
    if (typeof ts === 'string') return new Date(ts).toLocaleString();
    if (typeof ts.toDate === 'function') return ts.toDate().toLocaleString();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000).toLocaleString();
    return '-';
  } catch { return '-'; }
}

export const ConsoleHome: React.FC<ConsoleHomeProps> = ({
  onNavigateToProofExplorer,
  onNavigateToAdmin,
  onNavigateToCreateRequest,
  onNavigateToMarketplace,
  onNavigateToPolicyPacks,
  onNavigateToRequestsInbox,
  onNavigateToEpoch,
  onNavigateToCivilDisclosure,
  onNavigateToPdfEvidenceBundle,
  onNavigateToTraceExplorer,
  onNavigateToCryptoTest,
  onNavigateToVK1Architecture,
  isAdmin,
  tenantId,
}) => {
  const navigate = useNavigate();

  // SESSION_AWARE_VISIBILITY_GATING_V1
  const { health } = useWorkLifecycleContext();
  const [gwuCount, setGwuCount] = useState<number | null>(null);
  const showDemoPanel = !health.hasContext && gwuCount === 0;

  useEffect(() => {
    const user = auth?.currentUser;
    if (!user) { setGwuCount(0); return; }
    user.getIdToken()
      .then(token =>
        fetch('https://us-central1-trustgate-27ddc.cloudfunctions.net/listGWUs', {
          headers: { Authorization: `Bearer ${token}` },
        })
      )
      .then(r => r.json())
      .then(data => setGwuCount(Array.isArray(data.gwus) ? data.gwus.length : 0))
      .catch(() => setGwuCount(0));
  }, []);

  const [epoch, setEpoch] = useState<TrustGateEpoch | null>(null);
  const [epochError, setEpochError] = useState<string | null>(null);
  const [epochLoading, setEpochLoading] = useState(true);

  const [metrics, setMetrics] = useState<TrustGateMetricsECS>({
    eventsToday: 0,
    eventsByType: {} as Record<string, number>,
    eventsByStatus: {} as Record<string, number>,
    approvalRate: 0,
    pendingCount: 0,
    executedToday: 0,
    loading: true,
    error: null,
  });

  const [events, setEvents] = useState<TrustGateEvent[]>([]);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<TrustGateEvent | null>(null);

  const [blockedRequests, setBlockedRequests] = useState<BlockedRequest[]>([]);
  const [blockedLoading, setBlockedLoading] = useState(true);

  const [queueCount, setQueueCount] = useState<number | null>(null);
  const [blockedCount, setBlockedCount] = useState<number | null>(null);
  const [stuckCount, setStuckCount] = useState<number>(0);
  const [errors5mCount, setErrors5mCount] = useState<number>(0);
  const [errors1hCount, setErrors1hCount] = useState<number>(0);

  const [opsEvents, setOpsEvents] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [recentIssues, setRecentIssues] = useState<any[]>([]);
  const [activePolicyPack, setActivePolicyPack] = useState<ActivePolicyPack | null>(null);
  const [activePolicyPackLoading, setActivePolicyPackLoading] = useState(true);
  const [activePolicyPackError, setActivePolicyPackError] = useState<string | null>(null);

  const shortHash = (value: string | null | undefined): string => {
    if (!value) return '—';
    return value.length > 18 ? `${value.slice(0, 18)}…` : value;
  };

  useEffect(() => {
    setActivePolicyPackLoading(true);
    setActivePolicyPackError(null);

    return subscribeActivePolicyPack(
      tenantId,
      (value) => {
        setActivePolicyPack(value);
        setActivePolicyPackLoading(false);
      },
      (error) => {
        setActivePolicyPack(null);
        setActivePolicyPackError(error.message || 'Failed to load active policy pack');
        setActivePolicyPackLoading(false);
      }
    );
  }, [tenantId]);

  useEffect(() => {
    const q = query(
      collection(db, 'trustgate_requests'),
      where('status', '==', 'PENDING'),
      limit(100)
    );
    return onSnapshot(q, (snap) => setQueueCount(snap.size), () => setQueueCount(null));
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'trustgate_requests'),
      where('executionBlocked', '==', true),
      limit(100)
    );
    return onSnapshot(q, (snap) => setBlockedCount(snap.size), () => setBlockedCount(null));
  }, []);

  // Subscribe to ops events (last 200, for computing errors)
  useEffect(() => {
    const q = query(
      collection(db, 'trustgate_ops_events'),
      orderBy('ts', 'desc'),
      limit(200)
    );
    return onSnapshot(q, (snap) => {
      setOpsEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('[ConsoleHome] Ops events subscription error:', err));
  }, []);

  // Subscribe to requests (last 200, for computing stuck approvals)
  useEffect(() => {
    const q = query(
      collection(db, 'trustgate_requests'),
      orderBy('updatedAt', 'desc'),
      limit(200)
    );
    return onSnapshot(q, (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('[ConsoleHome] Requests subscription error:', err));
  }, []);

  // Helper: robust timestamp parser
  const parseTimestamp = (ts: any): number | null => {
    if (!ts) return null;
    if (typeof ts === 'number') return ts;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts.seconds === 'number') return ts.seconds * 1000;
    return null;
  };

  // Compute health metrics from ops events and requests
  useEffect(() => {
    const nowMs = Date.now();
    const last5m = nowMs - 5 * 60 * 1000;
    const last1h = nowMs - 60 * 60 * 1000;
    const leaseFreshMs = 10 * 60 * 1000;

    // Error events
    const isErrorEvent = (e: any) => {
      return e.eventType === 'EXECUTION_ERROR' || e.outcome === 'ERROR';
    };

    const errorEvents = opsEvents.filter((e) => {
      if (!isErrorEvent(e)) return false;
      const ts = parseTimestamp(e.ts);
      return ts !== null && ts >= last1h;
    });

    const errors5m = errorEvents.filter((e) => {
      const ts = parseTimestamp(e.ts);
      return ts !== null && ts >= last5m;
    });

    setErrors5mCount(errors5m.length);
    setErrors1hCount(errorEvents.length);

    // Stuck approvals (foundation-grade definition)
    const stuckApprovals = requests.filter((r) => {
      const statusApproved = r.status === 'APPROVED';
      if (!statusApproved) return false;

      const blocked = r.executionBlocked === true;
      if (blocked) return false;

      const leaseId = r.execution?.leaseId;
      const leaseAt = parseTimestamp(r.execution?.leaseAcquiredAt);
      const leaseAgeMs = leaseAt ? nowMs - leaseAt : null;
      const leaseExpired = leaseId && leaseAgeMs !== null && leaseAgeMs > leaseFreshMs;
      const hasLastError = !!r.execution?.lastError;
      const validUntil = parseTimestamp(r.decision?.validUntil);
      const approvalExpired = validUntil !== null && nowMs > validUntil;

      return hasLastError || leaseExpired || (leaseId == null && approvalExpired);
    });

    setStuckCount(stuckApprovals.length);

    // Build Recent Issues list
    const issues: any[] = [];

    // Add error issues
    errorEvents.slice(0, 10).forEach((e) => {
      const eventType = e.eventType || 'UNKNOWN';
      const reasonCode = e.reasonCode ? ` (${e.reasonCode})` : '';
      issues.push({
        kind: 'ERROR',
        tsMs: parseTimestamp(e.ts) || 0,
        requestId: e.requestId || '—',
        correlationId: e.correlationId || '—',
        title: 'Execution error',
        detail: `${eventType}${reasonCode}`,
      });
    });

    // Add stuck approval issues
    stuckApprovals.slice(0, 10).forEach((r) => {
      const leaseId = r.execution?.leaseId;
      const leaseAt = parseTimestamp(r.execution?.leaseAcquiredAt);
      const leaseAgeMs = leaseAt ? nowMs - leaseAt : null;
      const leaseExpired = leaseId && leaseAgeMs !== null && leaseAgeMs > leaseFreshMs;
      const hasLastError = !!r.execution?.lastError;
      const validUntil = parseTimestamp(r.decision?.validUntil);
      const approvalExpired = validUntil !== null && nowMs > validUntil;

      let stuckReason = 'UNKNOWN';
      if (hasLastError) stuckReason = 'LAST_ERROR';
      else if (leaseExpired) stuckReason = 'LEASE_EXPIRED';
      else if (approvalExpired) stuckReason = 'APPROVAL_EXPIRED';

      const tsMs = Math.max(
        parseTimestamp(r.updatedAt) || 0,
        parseTimestamp(r.execution?.leaseAcquiredAt) || 0,
        parseTimestamp(r.decision?.decidedAt) || 0
      );

      issues.push({
        kind: 'STUCK',
        tsMs,
        requestId: r.id,
        correlationId: r.execution?.correlationIdLastAttempt || r.decision?.correlationId || '—',
        title: 'Approved but stuck',
        detail: stuckReason,
      });
    });

    // Sort by timestamp descending and take top 5
    issues.sort((a, b) => b.tsMs - a.tsMs);
    setRecentIssues(issues.slice(0, 5));
  }, [opsEvents, requests]);

  // Subscribe to blocked governance events (recent)
  useEffect(() => {
    const q = query(
      collection(db, 'trustgate_requests'),
      where('executionBlocked', '==', true),
      orderBy('updatedAt', 'desc'),
      limit(5)
    );
    setBlockedLoading(true);
    return onSnapshot(q, (snap) => {
      setBlockedRequests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setBlockedLoading(false);
    }, () => setBlockedLoading(false));
  }, []);

  // Load epoch metadata (ECS) - Keep as periodic fetch (not real-time)
  useEffect(() => {
    const loadEpoch = async () => {
      try {
        setEpochLoading(true);
        setEpochError(null);
        const data = await fetchEpochECS();
        setEpoch(data);
      } catch (error) {
        console.error('[ConsoleHome] Failed to fetch epoch:', error);
        setEpochError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setEpochLoading(false);
      }
    };

    loadEpoch();
    // Refresh epoch every 60 seconds (less frequent than real-time data)
    const interval = setInterval(loadEpoch, 60000);
    return () => clearInterval(interval);
  }, []);

  // Subscribe to real-time metrics (ECS) with debouncing
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = subscribeToMetricsECS((newMetrics) => {
      // Debounce updates to prevent excessive re-renders
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setMetrics(newMetrics);
      }, 300); // 300ms debounce
    });

    // Cleanup: unsubscribe from Firestore listener
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, []);

  // Subscribe to real-time activity stream (ECS) with debouncing
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = subscribeToActivityStreamECS((stream) => {
      // Debounce updates to prevent excessive re-renders
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setEvents(stream.events);
        setEventsError(stream.error);
        setEventsLoading(stream.loading);
      }, 300); // 300ms debounce
    }, 10); // Limit to 10 most recent events

    // Cleanup: unsubscribe from Firestore listener
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* Persisted context bar — UNIFIED_WORK_LIFECYCLE_CONTEXT_PERSISTENCE_V1 */}
      <ActiveWorkItemBar currentStage="guided-flow" />

      {/* Resume Work panel — UNIFIED_WORK_LIFECYCLE_RESUME_PANEL_V1 */}
      <ResumeWorkPanel />

      {/* Demo-to-Private onboarding panel — only shown to new users with no context and no GWUs */}
      {showDemoPanel && <DemoToPrivatePanel />}

      {/* Work Lifecycle Entry — UNIFIED_WORK_INTAKE_TO_GWU_V1 */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-base font-semibold text-white">Start Governed Work</h2>
            <p className="text-xs text-slate-400 mt-0.5">Create a work item and follow it through governance, receipts, and verification</p>
          </div>
          {onNavigateToCreateRequest && (
            <button
              onClick={onNavigateToCreateRequest}
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Create Work Item
            </button>
          )}
        </div>
        <WorkLifecycleNav
          currentStage="guided-flow"
          context={{}}
          compact
        />
      </div>

      {/* Governance Status Card */}
      <Card
        title="🔐 Governance Status"
        error={!!epochError}
        className="hover:shadow-xl transition-shadow duration-200"
      >
        {epochLoading ? (
          <div className="space-y-3">
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
          </div>
        ) : epochError ? (
          <div className="text-center py-8">
            <div className="text-[#ef4444] text-lg mb-2">⚠️ Governance Status Unavailable</div>
            <div className="text-sm text-[#cbd5e1]">
              Unable to fetch epoch metadata. System may be unreachable.
            </div>
            <div className="text-xs text-[#cbd5e1] mt-2 font-mono">{epochError}</div>
          </div>
        ) : epoch ? (
          <div className="space-y-2">
            <MetricRow label="Epoch ID" value={epoch.epochId || '—'} mono />
            <MetricRow label="Deployed" value={formatTimestamp(epoch.deployedAt)} />
            <MetricRow label="Git SHA" value={epoch.gitSha?.slice(0, 12) || '—'} mono />
            <MetricRow
              label="Status"
              value={epoch.activeHash ? '✓ Active' : '⚠️ Unverified'}
              badge={!!epoch.activeHash}
            />
          </div>
        ) : null}
      </Card>

      <Card
        title="🧩 Active Policy Pack"
        error={!activePolicyPackLoading && (!!activePolicyPackError || !activePolicyPack)}
        className="hover:shadow-xl transition-shadow duration-200"
      >
        {activePolicyPackLoading ? (
          <div className="space-y-3">
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
            <div className="h-6 bg-white/5 animate-pulse rounded"></div>
          </div>
        ) : activePolicyPackError ? (
          <div className="text-center py-6">
            <div className="text-[#ef4444] text-lg mb-2">⚠️ Governance State Unavailable</div>
            <div className="text-sm text-[#cbd5e1]">Unable to load the active governing policy pack.</div>
            <div className="text-xs text-[#cbd5e1] mt-2 font-mono break-all">{activePolicyPackError}</div>
          </div>
        ) : !activePolicyPack ? (
          <div className="text-center py-6">
            <div className="text-[#ef4444] text-lg mb-2">⚠️ No Active Governing Policy</div>
            <div className="text-sm text-[#cbd5e1]">This tenant does not currently have an active policy pack. Governed actions are fail-closed until governance is configured.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <MetricRow label="Pack" value={activePolicyPack.packId || '—'} mono />
            <MetricRow label="Version" value={`v${activePolicyPack.version || '—'}`} mono />
            <MetricRow label="Status" value="ACTIVE" badge />
            <MetricRow label="Governance" value="Workspace governed under active policy pack" />
            {(activePolicyPack.canonicalHash || activePolicyPack.policyPackHash) && (
              <MetricRow
                label="Canonical Hash"
                value={shortHash(activePolicyPack.canonicalHash || activePolicyPack.policyPackHash)}
                mono
              />
            )}
            {onNavigateToPolicyPacks && (
              <div className="pt-3">
                <button
                  onClick={onNavigateToPolicyPacks}
                  className="px-3 py-1.5 rounded bg-[#00d4aa]/10 hover:bg-[#00d4aa]/20 border border-[#00d4aa]/30 text-[#00d4aa] text-sm"
                >
                  Open Policy Packs
                </button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Activity Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-[#f8fafc] mb-4">📊 Activity Metrics (Today)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricTile
            label="Events Today"
            value={metrics.eventsToday}
            loading={metrics.loading}
          />
          <MetricTile
            label="Approval Rate"
            value={`${metrics.approvalRate}%`}
            loading={metrics.loading}
          />
          <MetricTile
            label="Awaiting Binding"
            value={metrics.pendingCount}
            loading={metrics.loading}
          />
          <MetricTile
            label="Bound Intents Executed"
            value={metrics.executedToday}
            loading={metrics.loading}
          />
        </div>
        {metrics.error && (
          <div className="mt-4 p-3 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-lg text-sm text-[#ef4444]">
            ⚠️ Failed to load metrics: {metrics.error}
          </div>
        )}
      </div>

      {/* Health Card */}
      <Card title="💚 Health" className="hover:shadow-xl transition-shadow duration-200">
        <div className="overflow-x-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-4 min-w-max md:min-w-0">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#f59e0b]">{queueCount !== null ? queueCount : '—'}</div>
            <div className="text-xs text-[#cbd5e1]">Queue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#ef4444]">{blockedCount !== null ? blockedCount : '—'}</div>
            <div className="text-xs text-[#cbd5e1]">Blocked</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#8b5cf6]">{stuckCount}</div>
            <div className="text-xs text-[#cbd5e1]" title="Stuck: lease expired, last error, or approval expired">Stuck Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#f59e0b]">{errors5mCount}</div>
            <div className="text-xs text-[#cbd5e1]">Errors (5m)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#ef4444]">{errors1hCount}</div>
            <div className="text-xs text-[#cbd5e1]">Errors (1h)</div>
          </div>
          </div>
        </div>
        {recentIssues.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3">
            <div className="text-xs text-[#cbd5e1] mb-2 font-semibold">Recent Issues</div>
            <div className="space-y-1.5">
              {recentIssues.map((issue, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs bg-white/5 p-2 rounded hover:bg-white/10">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      issue.kind === 'ERROR' 
                        ? 'bg-[#ef4444]/10 border border-[#ef4444]/30 text-[#ef4444]'
                        : 'bg-[#f59e0b]/10 border border-[#f59e0b]/30 text-[#f59e0b]'
                    }`}>
                      {issue.kind}
                    </span>
                    <button
                      onClick={() => navigate(`/requests/${issue.requestId}`)}
                      className="text-[#00d4aa] hover:underline font-mono text-[10px] truncate"
                      title={issue.requestId}
                    >
                      {issue.requestId !== '—' ? `${issue.requestId.slice(0, 12)}...` : '—'}
                    </button>
                    {issue.correlationId !== '—' && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(issue.correlationId);
                        }}
                        className="text-[#cbd5e1] hover:text-[#f8fafc] font-mono text-[10px]"
                        title="Copy correlation ID"
                      >
                        📋 {issue.correlationId.slice(0, 8)}
                      </button>
                    )}
                  </div>
                  <span className="text-[10px] text-[#cbd5e1] ml-2 truncate max-w-[200px]" title={issue.detail}>
                    {issue.detail}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs mt-3">
          {isAdmin && (
            <button onClick={() => navigate('/ops')} className="px-3 py-1.5 rounded bg-[#00d4aa]/10 hover:bg-[#00d4aa]/20 border border-[#00d4aa]/30 text-[#00d4aa]">
              Open Ops Events
            </button>
          )}
          <button onClick={() => navigate('/requests')} className="px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-[#f8fafc]">
            Open Requests
          </button>
        </div>
      </Card>

      {/* Phase C — Canonical Operator Flow (descriptive projection only) */}
      <CanonicalFlowPanel />

      {/* Governance Events (Blocked) */}
      <Card title="⚠️ Governance Events (recent)" className="hover:shadow-xl transition-shadow duration-200">
        {blockedLoading ? (
          <div className="h-12 bg-white/5 animate-pulse rounded"></div>
        ) : blockedRequests.length === 0 ? (
          <div className="text-center py-4 text-[#cbd5e1] text-sm">No blocked requests</div>
        ) : (
          <div className="space-y-2">
            {blockedRequests.map((r) => {
              const memo = parseMemo(r.lockedParams?.memo);
              return (
                <div
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-[#ef4444]/5 border border-[#ef4444]/20 rounded-lg hover:bg-[#ef4444]/10 cursor-pointer active:scale-[0.98] transition-all"
                  onClick={() => navigate(`/requests/${r.id}`)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded border border-[#ef4444]/30 bg-[#ef4444]/10 text-[#ef4444] text-xs font-semibold">BLOCKED</span>
                    <span className="text-xs font-mono text-[#cbd5e1] truncate">{r.executionBlockReasonCode || '-'}</span>
                    {memo.packId && <span className="text-xs text-[#f8fafc] truncate">{memo.packId}@{memo.version}</span>}
                  </div>
                  <span className="text-xs text-[#cbd5e1] whitespace-nowrap">{safeDateLabel(r.updatedAt)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent Activity Stream */}
      <Card title="🔍 Recent Activity" className="hover:shadow-xl transition-shadow duration-200">
        {eventsLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-white/5 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : eventsError ? (
          <div className="text-center py-6">
            <div className="text-[#ef4444] text-base mb-2">⚠️ Unable to load recent activity</div>
            <div className="text-xs text-[#cbd5e1] mt-2 font-mono break-all px-4">{eventsError}</div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-6 text-[#cbd5e1] text-sm">No recent activity</div>
        ) : (
          <div className="space-y-2">
            {events.map((event) => (
              <div
                key={event.id}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-white/5 rounded-lg hover:bg-white/10 active:scale-[0.98] transition-all cursor-pointer"
                onClick={() => setSelectedEvent(event)}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Badge status={event.status} />
                  <span className="text-xs sm:text-sm font-mono text-[#cbd5e1] truncate">{event.entityId.slice(0, 12)}...</span>
                  <span className="text-xs sm:text-sm text-[#f8fafc] whitespace-nowrap">
                    {event.lockedParams ? formatCurrency(event.lockedParams.amount, event.lockedParams.currency) : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2 text-xs text-[#cbd5e1]">
                  <span className="truncate">→ {event.lockedParams?.destination || '—'}</span>
                  <span className="whitespace-nowrap">{formatRelativeTime(event.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Primary Actions */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3">
        <button
          onClick={() => setShowActionModal(true)}
          className="w-full sm:w-auto px-6 py-3 bg-[#00d4aa] hover:bg-[#00a8cc] active:bg-[#008a99] text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
        >
          + Create Bound Intent
        </button>
        {onNavigateToCreateRequest && (
          <button
            onClick={onNavigateToCreateRequest}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 active:from-purple-600 active:to-pink-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
          >
            🤖 Guided Request (Agent Mode)
          </button>
        )}
        {onNavigateToRequestsInbox && (
          <button
            onClick={onNavigateToRequestsInbox}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-700 hover:from-slate-400 hover:to-slate-600 active:from-slate-600 active:to-slate-800 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
          >
            � Requests Inbox
          </button>
        )}
        {onNavigateToPolicyPacks && (
          <button
            onClick={onNavigateToPolicyPacks}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 active:from-violet-600 active:to-fuchsia-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
          >
            🧩 Policy Pack Marketplace
          </button>
        )}
        {onNavigateToPdfEvidenceBundle && (
          <button onClick={onNavigateToPdfEvidenceBundle} className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl">📄 PDF Evidence Bundle</button>
        )}
        {onNavigateToVK1Architecture && (
          <button onClick={onNavigateToVK1Architecture} className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl">🏗️ VK1 Architecture</button>
        )}
        {onNavigateToCivilDisclosure && (
          <button onClick={onNavigateToCivilDisclosure} className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl">📋 Civil Disclosure Workspace</button>
        )}
        {onNavigateToTraceExplorer && (
          <button onClick={onNavigateToTraceExplorer} className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#2a3a6a] to-[#1e2848] hover:from-[#3a4a7a] hover:to-[#2a3858] border border-[#4868d0]/50 text-[#8090c8] rounded-xl font-semibold transition-all text-sm sm:text-base">⊢ Trace Explorer</button>
        )}
        {onNavigateToCryptoTest && (
          <button onClick={onNavigateToCryptoTest} className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl">🔐 Crypto Adapter Test</button>
        )}
        {onNavigateToMarketplace && (
          <button
            onClick={onNavigateToMarketplace}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-400 hover:to-indigo-400 active:from-blue-600 active:to-indigo-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
          >
            📦 Module Marketplace
          </button>
        )}
        {onNavigateToEpoch && isAdmin && (
          <button
            onClick={onNavigateToEpoch}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-400 hover:to-teal-400 active:from-green-600 active:to-teal-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-sm sm:text-base"
            title="Invite-only: Epoch Evolution for controlled version promotion"
          >
            🚀 Epoch Evolution
          </button>
        )}
      </div>

      {/* Action Request Modal */}
      <ActionRequestModal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        onSuccess={(requestId) => {
          console.log('[ConsoleHome] Request created:', requestId);
          // Real-time listener will auto-update the stream
        }}
      />

      {/* Quick Links */}
      <Card className="bg-white/5">
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={onNavigateToProofExplorer}
            className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
          >
            → Proof Explorer
          </button>
          <button
            onClick={() => window.location.href = '/formation-gate'}
            className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
          >
            → Formation Gate
          </button>
          <button
            onClick={() => window.location.href = '/formation-gate/console-preview'}
            className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
          >
            → Console Preview
          </button>
          <button
            onClick={() => window.location.href = '/formation-gate/verify'}
            className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
          >
            → Verify Receipt
          </button>
          <button
            onClick={() => window.location.href = '/formation-gate/workspace'}
            className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
          >
            → Private Workspace
          </button>
          {isAdmin && onNavigateToAdmin && (
            <button
              onClick={onNavigateToAdmin}
              className="px-4 py-2 text-[#00d4aa] hover:text-[#00a8cc] transition-colors font-medium"
            >
              → Admin Console
            </button>
          )}
        </div>
      </Card>

      {/* Bound Intent Detail View */}
      {selectedEvent && (
        <BoundIntentDetailView
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};
